import { useSearchBrandsMutation } from "@api/media";
import { useLazyGetStoryBrainQuery } from "@api/story-brain";
import type { StoryType } from "@config";
import { toBrandImportSnapshot } from "@editor/modules/design-system/modules/brand/utils/brand-import-snapshot";
import { useAccountStoreShallow } from "@features/account-management";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate as useRouterNavigate } from "react-router-dom";
import { useCreateStory } from "@/providers/hooks";
import {
  standaloneVersionEditorPath,
  useCreateStandaloneVersion,
} from "@/features/standalone-versions";
import { DEFAULT_STORY } from "@/shared/constants";
import type { FunnelCompletePayload } from "../funnel-mode";
import { usePlatformFunnelStore } from "../platform-funnel.store";
import { useBrandPrefillStore } from "../store/brand-prefill.store";
import { waitForBrandSave } from "../store/pending-brand-save";
import { applyStyling } from "../utils/apply-styling";
import { applyBrandLogo } from "../utils/apply-brand-logo";
import { createRunTracker } from "../utils/run-tracker";

export type CompleteFunnelInput = FunnelCompletePayload & {
  title?: string;
  /**
   * No "created" toast. The streamed deck is created under a provisional
   * title and announces itself when the last slide lands instead.
   */
  skipToast?: boolean;
};

// Cap on the best-effort AI-Brain brand lookup before story creation proceeds
// without a preselected brand. (Moved from platform-funnel-overlay.tsx.)
const BRAIN_BRAND_LOOKUP_TIMEOUT_MS = 6000;

/**
 * The single completion path for every funnel flow (platform template,
 * platform AI deck, signup Ready step, blank story): waits briefly for a
 * pending brand-brain save, applies the payload styling, attaches a brand
 * snapshot, creates the story, then closes the funnel and opens the editor.
 *
 * Returns what it created, for the one caller that is not finished when this
 * resolves: the AI deck creates a story holding only its first slide and then
 * streams the rest into the open editor (see ../deck-build-runner), which
 * needs the story's id to fill and the resolved brand to keep putting the
 * user's logo on the slides that arrive later. Every other caller ignores it.
 *
 * Cancellation: `cancelCompletion()` (wired to unmount and Go back/close)
 * makes an in-flight run stale — the story may still be created server-side,
 * but the funnel will neither close itself nor navigate against the user's
 * expressed intent.
 *
 * Returns what it created, for the one caller that is not finished when this
 * resolves: the platform's AI deck creates the story first and then streams
 * every slide into the open editor (see ../deck-build-runner), which needs
 * the story's id to fill and the resolved brand to keep putting the user's
 * logo on the slides that arrive. Every other caller ignores it.
 */
export const useCompleteFunnel = () => {
  const createStory = useCreateStory();
  const createStandaloneVersion = useCreateStandaloneVersion();
  const routerNavigate = useRouterNavigate();
  const closeFunnel = usePlatformFunnelStore((s) => s.closeFunnel);
  // A story, or a standalone version (see features/standalone-versions).
  // Read from the store: this hook lives outside the funnel's own tree.
  const target = usePlatformFunnelStore((s) => s.target);
  const accountStoryBrainId = useAccountStoreShallow(
    (s) => s.account?.accountStoryBrainId,
  );
  const [getStoryBrain] = useLazyGetStoryBrainQuery();
  const [searchBrands] = useSearchBrandsMutation();
  const [isCompleting, setIsCompleting] = useState(false);
  const trackerRef = useRef(createRunTracker());

  // A completion started from a step that unmounts (Go back mid-flight) must
  // not navigate later.
  useEffect(() => {
    const tracker = trackerRef.current;
    return () => tracker.cancel();
  }, []);

  const cancelCompletion = useCallback(() => {
    trackerRef.current.cancel();
    setIsCompleting(false);
  }, []);

  // Best-effort, time-bounded resolution of the account AI-Brain brand into
  // the Brandfetch snapshot Brand Import expects. (Moved verbatim from
  // platform-funnel-overlay.tsx's resolveBrainBrandImport.)
  const resolveBrainBrandImport = useCallback(async () => {
    if (!accountStoryBrainId) return undefined;
    const lookup = async () => {
      const brain = await getStoryBrain(
        { id: accountStoryBrainId },
        true,
      ).unwrap();
      const domain = brain?.brand?.domain;
      if (!domain) return undefined;
      const full = await searchBrands({
        domain,
        disableRateLimit: false,
      }).unwrap();
      return (
        toBrandImportSnapshot({
          ...(full as Record<string, unknown>),
          domain,
        } as Parameters<typeof toBrandImportSnapshot>[0]) ?? undefined
      );
    };
    try {
      return await Promise.race([
        lookup(),
        new Promise<undefined>((resolve) =>
          setTimeout(() => resolve(undefined), BRAIN_BRAND_LOOKUP_TIMEOUT_MS),
        ),
      ]);
    } catch {
      return undefined;
    }
  }, [accountStoryBrainId, getStoryBrain, searchBrands]);

  /**
   * The brand whose logo and colours the created story carries: the one picked
   * in this funnel run, else the account's AI-Brain brand. Exposed so the
   * Ready step's preview can show the same logo the story will open with.
   */
  const resolveBrandImport = useCallback(
    async () =>
      useBrandPrefillStore.getState().brand?.brandImport ??
      (await resolveBrainBrandImport()),
    [resolveBrainBrandImport],
  );

  const completeFunnel = useCallback(
    async (
      payload: CompleteFunnelInput,
    ): Promise<{
      storyId: string;
      /** Set when the run made a standalone version; the story is its host. */
      versionId: string | null;
      brandImport: unknown;
    } | null> => {
      const runId = trackerRef.current.begin();
      setIsCompleting(true);
      try {
        // A brand save started on the brand step may still be in flight; the
        // created story copies the account brain, so give it a moment to land.
        await waitForBrandSave();

        const base = payload.templateStory ?? structuredClone(DEFAULT_STORY);
        const template = applyStyling(
          payload,
          base as unknown as typeof DEFAULT_STORY,
        );
        const settings = template.settings as Record<string, unknown>;
        if (payload.customColors?.length) {
          settings.customColors = payload.customColors;
        }
        if (!settings.brandImport) {
          // Prefer the brand picked in this funnel run; fall back to the
          // account AI-Brain brand (previously only the template path did this).
          settings.brandImport = await resolveBrandImport();
        }

        // THE USER'S LOGO, NOT THE TEMPLATE'S PLACEHOLDER. The brand is known
        // by now and attached above, and every template ships a "Logoipsum"
        // stand-in that otherwise survives into the finished document. See
        // ../utils/apply-brand-logo.
        if (Array.isArray(template.slides)) {
          template.slides = applyBrandLogo(
            template.slides,
            settings.brandImport as Parameters<typeof applyBrandLogo>[1],
            settings.designSystemV2 as Record<string, unknown> | undefined,
          ).slides;
        }
        if (!trackerRef.current.isCurrent(runId)) return null;

        // A STANDALONE VERSION instead of a story: the same styled story JSON
        // becomes a version of the user's hidden host page, and the editor
        // opens on the version. The deck runner fills it the same way.
        if (target === "version") {
          const made = await createStandaloneVersion({
            template: template as unknown as StoryType,
            title: payload.title || payload.docType || "Untitled version",
            // Where it came from, for the Versions page's badge. A caller
            // that knows says so; otherwise the template it was built on
            // answers -- a stored one, or slides assembled in the browser --
            // and a document built on nothing is blank.
            origin:
              payload.origin ??
              (payload.templateId
                ? { kind: "template" }
                : payload.templateStory
                  ? { kind: "slides" }
                  : { kind: "blank" }),
            basePageId: payload.basePageId ?? null,
            keepTemplateDesignSystem: payload.keepTemplateDesignSystem ?? true,
            skipToast: payload.skipToast,
          });
          if (!trackerRef.current.isCurrent(runId)) return null;
          closeFunnel();
          routerNavigate(
            standaloneVersionEditorPath(made.page._id, made.version._id),
          );
          return {
            storyId: made.page._id,
            versionId: made.version._id,
            brandImport: settings.brandImport,
          };
        }

        const created = await createStory({
          status: "Draft",
          title: payload.title || payload.docType || "Untitled document",
          template: template as unknown as StoryType,
          type: "create",
          forceUseTemplateSlides: true,
          // Styling picked in the funnel is the user's explicit choice; paths
          // with no styling of their own pass false so the account theme applies.
          keepTemplateDesignSystem: payload.keepTemplateDesignSystem ?? true,
          skipToast: payload.skipToast,
        });
        if (!created?._id) throw new Error("Failed to create story");
        if (!trackerRef.current.isCurrent(runId)) return null;

        closeFunnel();
        routerNavigate(`/pages/editor/${created._id}?focusTitle=1`);
        return {
          storyId: created._id,
          versionId: null,
          brandImport: settings.brandImport,
        };
      } finally {
        if (trackerRef.current.isCurrent(runId)) setIsCompleting(false);
      }
    },
    [
      createStory,
      createStandaloneVersion,
      target,
      resolveBrandImport,
      closeFunnel,
      routerNavigate,
    ],
  );

  return { completeFunnel, cancelCompletion, isCompleting, resolveBrandImport };
};
